import { Observable } from "rxjs";
import { first } from "rxjs/operators";
import { DateTime } from 'luxon'
import _ from "lodash";

import { DataStoreOptions, DataSynchronisationStatus, Expression, Item, Manifestation, SearchQuery, SearchResponse, SearchResult, Wemi, Work } from "@skinsoft/types";
import { getItemsToSyncAuditCampaign, getItemsToSyncBatches } from "./synchro_utils";
import { searchResultToWemi } from "@skinsoft/type-converters";

import { CACHE_KEYS, ExpressionOfflineDataFetcher, ItemOfflineDataFetcher, ManifestationOfflineDataFetcher, WorkOfflineDataFetcher } from "@skinsoft/offline";
import { ItemDataStore } from "@skinsoft/entity";
import { AbstractSyncDownHandler } from "@skinsoft/synchro-down-handlers"
import { cleanAssociatedMedias } from "@skinsoft/utils";


declare const APP_NAME: string
declare const config

const ITEM_SEARCH_TYPES: string[] = config.item_search_types
const SEARCH_PAGE_SIZE = config.syncDownPageSize.wemi ?? config.syncDownPageSize.default

export class WemiSynchroDownHandler extends AbstractSyncDownHandler<SearchResult, SearchResponse<SearchResult>, Wemi> {
    private itemOfflineDataFetcher: ItemOfflineDataFetcher
    private manifestationOfflineDataFetcher: ManifestationOfflineDataFetcher
    private expressionOfflineDataFetcher: ExpressionOfflineDataFetcher
    private workOfflineDataFetcher: WorkOfflineDataFetcher

    private idsToSynchDown: string[] = []

    get mainCacheKey(): string {
        return CACHE_KEYS.Item
    }

    constructor() {
        super(ITEM_SEARCH_TYPES, SEARCH_PAGE_SIZE)
        this.itemOfflineDataFetcher = new ItemOfflineDataFetcher(CACHE_KEYS.Item)
        this.manifestationOfflineDataFetcher = new ManifestationOfflineDataFetcher(CACHE_KEYS.Manifestation)
        this.expressionOfflineDataFetcher = new ExpressionOfflineDataFetcher(CACHE_KEYS.Expression)
        this.workOfflineDataFetcher = new WorkOfflineDataFetcher(CACHE_KEYS.Work)
    }

    private async clearDataFetchers() {
        await this.itemOfflineDataFetcher.clear()
        await this.manifestationOfflineDataFetcher.clear()
        await this.expressionOfflineDataFetcher.clear()
        await this.workOfflineDataFetcher.clear()
    }

    async clearCacheDown() {
        await this.clearDataFetchers()
    }
    
    async beforeSynchDown() {
        this.executionStartTime = DateTime.now()
        this.shouldAbortSync = false
        this.currentNbSychronized = 0
        this.syncing = true
        this.saveStats(DataSynchronisationStatus.SUCCESS)  
        
        this.idsToSynchDown = []
        try {
            this.idsToSynchDown = await getItemsToSyncBatches().pipe(first()).toPromise()
        } catch(err) {
            this.abortSynchronisation()
        }

        if(APP_NAME === "AUDIT") {
            try{
                const idsInAuditProjects = await getItemsToSyncAuditCampaign().pipe(first()).toPromise()
                this.idsToSynchDown = this.idsToSynchDown.concat(idsInAuditProjects)
            } catch(err) {
                this.abortSynchronisation()
            }
        }

        this.idsToSynchDown = _.uniq(this.idsToSynchDown)
    }

    sendSynchroDownRequest(searchQuery: SearchQuery, options: DataStoreOptions): Observable<SearchResponse<SearchResult>> {
        searchQuery.filteredIds = this.idsToSynchDown
        return ItemDataStore.getInstance().paginatedSearch(searchQuery, options) as Observable<SearchResponse<SearchResult>>
    }

    getTotalNbToSync(response: SearchResponse<SearchResult>): number {
        return response.totalSize
    }

    getNbResultsInResponse(response: SearchResponse<SearchResult>): number {
        return response.results.length
    }

    unpackSynchDownResponse(response: SearchResponse<SearchResult>): SearchResult[] {
        return response.results
    }

    formatDataForCaching(rawData: SearchResult[]): Wemi[] {
        return rawData.map(searchResult => {
            const wemi = searchResultToWemi(searchResult)

            // clean associatedMedias
            if(wemi.item?.associatedMedias) {
                wemi.item.associatedMedias = cleanAssociatedMedias(wemi.item.associatedMedias)
            }

            return wemi
        })
    }

    async saveSynchDownDataInCache(data: Wemi[]) {
        let items: Item[] = []
        let manifestations: Manifestation[] = []
        let expressions: Expression[] = []
        let works: Work[] = []

        for(const wemi of data) {
            if(wemi.item) {
                items.push(wemi.item)
            }
            if(wemi.manifestation) {
                manifestations.push(wemi.manifestation)
            }
            if(wemi.expression) {
                expressions.push(wemi.expression)
            }
            if(wemi.work) {
                works.push(wemi.work)
            }
        }
        
        // @ts-ignore TODO fix typing of ItemOfflineDataFetcher (<S> param)
        await this.itemOfflineDataFetcher.append(items)
        await this.manifestationOfflineDataFetcher.append(manifestations)
        // @ts-ignore TODO fix typing of ExpressionOfflineDataFetcher (<S> param)
        await this.expressionOfflineDataFetcher.append(expressions)
        await this.workOfflineDataFetcher.append(works)
    }

    saveStats(status: DataSynchronisationStatus) {
        this.itemOfflineDataFetcher.saveStats({
            currentNbSychronized: this.currentNbSychronized,
            lastSynchroDate: DateTime.now().toMillis(),
            lastSynchroDuration: this.executionStartTime ? this.executionStartTime.diffNow().toMillis() : 0,
            lastSynchroStatus: status,
            maxNbToSynchronize: this.maxNbToSynchronize,
        })
    }

    abortSynchronisation() {
        this.shouldAbortSync = true
    }

    isSyncing() {
        return this.syncing
    }

    getLastSyncStats() {
        return this.itemOfflineDataFetcher.getLastSynchroStats()
    }
}